import { useResize } from '@hooks/useResize';
import { unit } from '@lib/style/unit';
import { useScroll } from '@packages/events/hooks/useScroll';
import { between, call } from '@packages/functions';
import { useCallback, useEffect, useMemo, useRef } from 'react';

type offsetFunc = () => number;

interface StickyOptions {
    offsetTop: number | offsetFunc;
    offsetLeft: number | offsetFunc;
    minWidth: number;
    method: 'css' | 'js';
}

/**
 * Compute offset or return 0 value
 * @param offsetFn
 */
const getOffset = (offsetFn?: number | offsetFunc) => (typeof offsetFn === 'number' ? offsetFn : call(offsetFn)) || 0;

/**
 * Detect if element can stick
 * @param minWidth
 */
const canStick = (minWidth = 0) =>
    typeof window !== 'undefined' && typeof minWidth !== 'undefined' && window.innerWidth > minWidth;

export const useSticky = ({ offsetTop, offsetLeft, minWidth, method = 'js' }: Partial<StickyOptions> = {}) => {
    const scroll = useScroll();
    const element = useRef<HTMLElement | null>(null);

    const elementOffset = useMemo(() => {
        let top = 0;
        let left = 0;

        if (element.current) {
            const el = element.current;
            const parent = (el.parentNode || el) as any;

            top = el.offsetTop || parent.offsetTop || 0;
            left = el.offsetLeft || parent.offsetLeft || 0;
        }

        return { top, left };
    }, [element.current]);

    const cleanSticky = useCallback(() => {
        if (element.current) {
            if (method === 'css') {
                element.current.style.position = '';
                element.current.style.top = '';
                element.current.style.left = '';
                return;
            }

            element.current.classList.remove('is-sticky');
            element.current.style.position = '';
            element.current.style.transform = '';
            element.current.style.transition = '';
            element.current.style.top = '';
            element.current.style.left = '';
            element.current.style.width = '';

            const parentNode = element.current.parentNode;
            if (parentNode) {
                // @ts-ignore
                parentNode.style.paddingTop = '';
            }
        }
    }, [element.current]);

    useResize(() => {
        if (!canStick(minWidth)) {
            cleanSticky();
        }
    });

    useEffect(() => {
        if (!canStick(minWidth)) {
            cleanSticky();
            return;
        }

        if (element.current) {
            const offsetLeft$ = getOffset(offsetLeft);
            const offsetTop$ = getOffset(offsetTop);

            if (method === 'css') {
                element.current.style.position = 'sticky';
                element.current.style.top = `${unit(offsetTop$)}`;
                element.current.style.left = `${unit(offsetLeft$)}`;
                return;
            }

            const parentNode = element.current.parentNode;
            const isSticky = element.current.classList.contains('is-sticky');

            const { top, left } = elementOffset;

            const scrollLeft = scroll.x + offsetLeft$;
            const scrollTop = scroll.y + offsetTop$;

            if ((scroll.y && scrollTop > top) || (scroll.x && scrollLeft > left)) {
                if (isSticky) {
                    return;
                }

                if (parentNode) {
                    // @ts-ignore
                    parentNode.style.paddingTop = `${unit((parentNode as any).clientHeight + between(offsetTop$, 0))}`;
                    element.current.style.width = `${unit((parentNode as any).clientWidth)}`;
                }

                element.current.classList.add('is-sticky');
                element.current.style.position = 'fixed';

                if (scrollTop > top) {
                    element.current.style.top = `${unit(top + offsetTop$)}`;
                }

                if (scrollLeft > left) {
                    element.current.style.left = `${unit(left + offsetLeft$)}`;
                }
            } else {
                if (!isSticky) {
                    return;
                }

                cleanSticky();
            }
        }
    }, [scroll.y, scroll.x, minWidth, offsetLeft, offsetTop, cleanSticky, elementOffset]);

    return {
        element,
        parent: scroll.element,
    };
};
